import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SEO } from "@/components/SEO";
import { ArrowLeft, Loader2 } from "lucide-react";

interface LegalDoc {
  slug: string;
  version: string;
  title: string;
  content_md: string;
  effective_at: string;
}

const ALLOWED_SLUGS = [
  "terms",
  "privacy",
  "cookies",
  "b2b-contract",
  "dpa",
  "caregiver-terms",
  "patient-consent",
  "retention",
  "security",
] as const;

const SLUG_TITLES: Record<string, string> = {
  terms: "Termos de Uso",
  privacy: "Política de Privacidade",
  cookies: "Política de Cookies",
  "b2b-contract": "Contrato de Prestação de Serviços B2B",
  dpa: "Acordo de Processamento de Dados (DPA)",
  "caregiver-terms": "Termo de Uso do Cuidador",
  "patient-consent": "Termo de Consentimento do Paciente",
  retention: "Política de Retenção e Eliminação de Dados",
  security: "Política de Segurança da Informação",
};

export default function LegalDocPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const normalized = (slug ?? "").toLowerCase();
    if (!ALLOWED_SLUGS.includes(normalized as typeof ALLOWED_SLUGS[number])) {
      setError("Documento não encontrado.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("legal_documents")
        .select("slug, version, title, content_md, effective_at")
        .eq("slug", normalized)
        .eq("locale", "pt-BR")
        .is("superseded_at", null)
        .order("effective_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        setError("Não foi possível carregar o documento.");
      } else if (!data) {
        setError("Documento indisponível no momento.");
      } else {
        setDoc(data as LegalDoc);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const title = doc?.title ?? SLUG_TITLES[slug ?? ""] ?? "Documento legal";

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={`${title} · CareOS`}
        description={`${title} da plataforma CareOS.`}
      />

      <header className="border-b border-border bg-card/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          <h1 className="text-sm font-medium text-muted-foreground truncate">
            {title}
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <div className="pt-4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-3/5" />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">{error}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/")}
            >
              Voltar ao início
            </Button>
          </div>
        ) : doc ? (
          <article className="prose prose-neutral dark:prose-invert max-w-none">
            <div className="mb-6 text-xs text-muted-foreground">
              Versão {doc.version} · Em vigor desde{" "}
              {new Date(doc.effective_at).toLocaleDateString("pt-BR")}
            </div>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {doc.content_md}
            </ReactMarkdown>
          </article>
        ) : (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}
      </main>
    </div>
  );
}
